import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "./StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "./useStaffAuthContext";
import { Link } from "react-router-dom";
import { LiaEditSolid } from "react-icons/lia";
import { RiDeleteBin4Line } from "react-icons/ri";
import { HiOutlinePlusSm } from "react-icons/hi";
import { MdKeyboardArrowLeft } from "react-icons/md";
import { MdKeyboardArrowRight } from "react-icons/md";

export const StaffManage = () => {
  const { staff } = useStaffAuthContext();

  const [staffData, setStaffData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    _id: "",
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    role: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  // ------------------ Fetch Staff ------------------
  const fetchStaffData = async (page) => {
    try {
      setIsLoading(true);
      setError("");
      const response = await fetch(
        API_URL.GET_STAFF_ALL + `?page=${page}&limit=50`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${staff.token}` },
        },
      );
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || "Failed to fetch data");

      setStaffData(data.users);
      setFilteredData(data.users);
      setCurrentPage(data.page);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStaffData(currentPage);
  }, [currentPage]);

  // ------------------ Search ------------------
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) return setFilteredData(staffData);

    const lower = query.toLowerCase();
    const filtered = staffData.filter(
      (s) =>
        s.firstname.toLowerCase().includes(lower) ||
        s.lastname.toLowerCase().includes(lower) ||
        s.email.toLowerCase().includes(lower) ||
        (s.role && s.role.toLowerCase().includes(lower)),
    );
    setFilteredData(filtered);
  };

  // ------------------ Delete ------------------
  const deleteStaff = async (id) => {
    try {
      setIsDeleting(true);
      const response = await fetch(API_URL.DELETE_STAFF + `/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${staff.token}` },
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || "Failed to delete staff");

      alert("Staff member deleted successfully!");
      fetchStaffData(currentPage);
    } catch (err) {
      alert(`Error deleting staff: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this staff member?")) {
      deleteStaff(id);
    }
  };

  // ------------------ Edit Logic ------------------
  const openEditModal = (s) => {
    setEditForm({
      _id: s._id,
      firstname: s.firstname,
      lastname: s.lastname,
      email: s.email,
      password: "",
      role: s.role || "staff",
    });
    setIsEditing(true);
    setSuccessMsg("");
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    setSuccessMsg("");
    setError("");

    try {
      const response = await fetch(API_URL.ADMIN_EDIT_STAFF + editForm._id, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify(editForm),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to update staff");

      setSuccessMsg("Staff details updated successfully!");
      setIsEditing(false);
      fetchStaffData(currentPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  // ------------------ Pagination ------------------
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // ------------------ JSX ------------------
  return (
    <div className="flex md:h-screen">
      <StaffSideMenu />

      <div className="max-md:w-full md:flex-1 bg-bgBase pb-10">
        <div className="bg-white">
          <div className="w-full max-w-screen-2xl space-y-6 pt-5 pb-3 md:py-6 px-5 md:px-10 md:sticky md:top-0">
            <div className="mb-3 md:mb-6 max-md:pl-14">
              <h1 className="font-inter font-medium text-dark text-2xl !leading-none">
                Manage Staff
              </h1>
            </div>

            <div className="w-full flex flex-wrap justify-between gap-2 md:gap-5 mb-4">
              <input
                type="text"
                placeholder="Search by name, email or role"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="max-md:min-w-56 lg:w-1/4 p-2 md:p-3 border border-black/10 rounded-lg font-inter font-normal text-sm"
              />
              <Link
                to="/administrator/staff/create"
                className="px-6 py-2.5 flex items-center gap-2 bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg hover:bg-black"
              >
                <HiOutlinePlusSm color="#fff" size={16} /> Create Staff Account
              </Link>
            </div>
          </div>
        </div>

        <div className="w-full max-w-screen-2xl py-4 md:py-8 px-5 md:px-10">
          {error && <p className="text-red-500 mb-2">{error}</p>}
          {successMsg && <p className="text-green-600 mb-2">{successMsg}</p>}

          <div className="w-full overflow-auto show-scrollbar max-h-[550px]">
            <div className="rounded-10 border border-tblBorder overflow-hidden w-full inline-table">
              <table className="table-auto w-full min-w-[640px]">
                <thead className="bg-tblHead border-b border-tblBorder text-slate100 font-inter !font-medium text-xs">
                  <tr className="text-slate100 font-inter !font-medium text-xs">
                    <th className="md:text-sm py-1.5 md:py-3 px-3 md:px-6 text-left">
                      First Name
                    </th>
                    <th className="md:text-sm py-1.5 md:py-3 px-3 md:px-6 text-left">
                      Last Name
                    </th>
                    <th className="md:text-sm py-1.5 md:py-3 px-3 md:px-6 text-left">
                      Email
                    </th>
                    <th className="md:text-sm py-1.5 md:py-3 px-3 md:px-6 text-left">
                      Role
                    </th>
                    <th className="md:text-sm py-1.5 md:py-3 px-3 md:px-6 text-left">
                      Created At
                    </th>
                    <th className="md:text-sm py-1.5 md:py-3 px-3 md:px-6 text-left">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {isLoading ? (
                    <tr className="bg-white font-inter font-normal text-slate100 text-xs md:text-sm">
                      <td
                        colSpan="6"
                        className="text-center py-4 text-blue-600"
                      >
                        Loading data...
                      </td>
                    </tr>
                  ) : filteredData.length === 0 ? (
                    <tr className="bg-white font-inter font-normal text-slate100 text-xs md:text-sm">
                      <td
                        colSpan="6"
                        className="text-center py-4 font-inter font-normal text-dark text-xl"
                      >
                        No records found
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((s, i) => (
                      <>
                        <tr
                          key={s._id}
                          className={`bg-white font-inter font-normal text-slate100 text-xs md:text-sm`}
                        >
                          <td className="px-3 md:px-6 py-2 md:py-4 capitalize">
                            {s.firstname}
                          </td>
                          <td className="px-3 md:px-6 py-2 md:py-4 capitalize">
                            {s.lastname}
                          </td>
                          <td className="px-3 md:px-6 py-2 md:py-4 text-dark">
                            {s.email}
                          </td>
                          <td className="px-3 md:px-6 py-2 md:py-4 capitalize">
                            {s.role === "master_admin"
                              ? "Master Admin"
                              : "Staff"}
                          </td>
                          <td className="px-3 md:px-6 py-2 md:py-4">
                            {new Date(s.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-3 md:px-6 py-2 md:py-4 flex gap-2">
                            <button
                              onClick={() => openEditModal(s)}
                              className="p-2 rounded-md hover:bg-slate-100"
                            >
                              <LiaEditSolid color="#666666" size={20} />
                            </button>
                            <button
                              onClick={() => handleDelete(s._id)}
                              disabled={isDeleting}
                              className="p-2 rounded-md hover:bg-slate-100"
                            >
                              <RiDeleteBin4Line color="#666666" size={20} />
                            </button>
                          </td>
                        </tr>
                      </>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex max-sm:flex-col justify-center sm:justify-between items-center mt-4">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 min-w-28 flex justify-center items-center gap-1 rounded-lg text-[#99A1AF] font-inter font-normal text-sm ${
                currentPage === 1 ? "cursor-not-allowed" : "text-black"
              }`}
            >
              <MdKeyboardArrowLeft size={20} /> Previous
            </button>
            <p className="text-dark font-inter font-normal text-sm ">
              Page {currentPage} of {totalPages}
            </p>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 min-w-28 flex justify-center items-center gap-1 rounded-lg text-[#99A1AF] font-inter font-normal text-sm ${
                currentPage === totalPages ? "cursor-not-allowed" : "text-black"
              }`}
            >
              Next <MdKeyboardArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* ---------- Edit Modal ---------- */}
      {isEditing && (
        <div className="fixed inset-0 flex justify-center items-center px-5 bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-10 shadow-lg w-full max-w-xl">
            <div className="relative py-5 px-6 border-b border-tblBorder">
              <h2 className="text-2xl leading-none capitalize font-inter font-medium text-dark">
                Edit Staff
              </h2>
            </div>
            <div className="p-6">
              <form
                onSubmit={handleSaveEdit}
                className="flex flex-col gap-5 text-sm font-inter font-normal leading-tight"
              >
                <input
                  type="text"
                  name="firstname"
                  value={editForm.firstname}
                  onChange={handleEditChange}
                  placeholder="First Name"
                  className="border border-tblBorder p-3 rounded-10 placeholder:text-slate100"
                />
                <input
                  type="text"
                  name="lastname"
                  value={editForm.lastname}
                  onChange={handleEditChange}
                  placeholder="Last Name"
                  className="border border-tblBorder p-3 rounded-10 placeholder:text-slate100"
                />
                <input
                  type="email"
                  name="email"
                  value={editForm.email}
                  onChange={handleEditChange}
                  placeholder="Email"
                  className="border border-tblBorder p-3 rounded-10 placeholder:text-slate100"
                />
                <input
                  type="password"
                  name="password"
                  value={editForm.password}
                  onChange={handleEditChange}
                  placeholder="New Password (optional)"
                  className="border border-tblBorder p-3 rounded-10 placeholder:text-slate100"
                />
                <select
                  name="role"
                  value={editForm.role}
                  onChange={handleEditChange}
                  className="border border-tblBorder p-3 rounded-10 placeholder:text-slate100 outline-none"
                >
                  <option value="staff">Staff</option>
                  <option value="master_admin">Master Admin</option>
                </select>

                <div className="flex flex-wrap justify-end gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="bg-transparent border border-tblBorder rounded-10 py-2.5 px-5 text-dark font-inter font-medium text-sm leading-tight capitalize hover:bg-dark hover:text-white transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingEdit}
                    className="bg-dark border border-tblBorder rounded-10 py-2.5 px-5 text-white font-inter font-medium text-sm leading-tight capitalize hover:bg-black transition"
                  >
                    {savingEdit ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
